import React, { createContext, useContext, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDebounce } from '../../hooks/useDebounce';
import { useMobileNavigate } from './MobileNavigationGuard';

type ListMemory = { pages: number; scroll: number; collapsed: string[] };
const Context = createContext<Map<string, ListMemory> | null>(null);
export function MobileListProvider({ children }: { children: React.ReactNode }) {
  const memory = useRef(new Map<string, ListMemory>());
  return <Context.Provider value={memory.current}>{children}</Context.Provider>;
}

export function normalizedListUrl(pathname: string, params: URLSearchParams) {
  const next = new URLSearchParams(params);
  next.sort();
  return `${pathname}${next.size ? `?${next}` : ''}`;
}

export function useMobileListControls<T extends string>(module: string, values: readonly T[], fallback: T, param = 'filter') {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const rawFilter = params.get(param);
  const filter = values.includes(rawFilter as T) ? rawFilter as T : fallback;
  const search = params.get('q') || '';
  const debouncedSearch = useDebounce(search.trim(), 300);
  const canonical = new URLSearchParams(params);
  canonical.set(param, filter);
  if (search.trim()) canonical.set('q', search.trim()); else canonical.delete('q');
  const key = `${module}:${normalizedListUrl(location.pathname, canonical)}`;
  const context = useContext(Context);
  const local = useRef(new Map<string, ListMemory>());
  const memory = context ?? local.current;
  if (!memory.has(key)) memory.set(key, { pages: 1, scroll: 0, collapsed: [] });
  const entry = memory.get(key)!;
  const valuesKey = JSON.stringify(values);
  const setParam = useCallback((name: string, value: string) => {
    const next = new URLSearchParams(location.search);
    if (value) next.set(name, value); else next.delete(name);
    const normalized = new URLSearchParams(next);
    normalized.set(param, (JSON.parse(valuesKey) as T[]).includes(normalized.get(param) as T) ? normalized.get(param)! : fallback);
    const query = (normalized.get('q') || '').trim();
    if (query) normalized.set('q', query); else normalized.delete('q');
    const nextKey = `${module}:${normalizedListUrl(location.pathname, normalized)}`;
    if (nextKey !== key) memory.delete(nextKey);
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true, state: location.state });
  }, [location.pathname, location.search, location.state, navigate, valuesKey, fallback, param, module, key, memory]);
  useEffect(() => {
    if (rawFilter !== null && rawFilter !== filter) setParam(param, filter);
  }, [rawFilter, filter, param, setParam]);
  return { search, setSearch: (value: string) => setParam('q', value), debouncedSearch,
    pending: search.trim() !== debouncedSearch, filter, setFilter: (value: T) => setParam(param, value), key, entry };
}

/** Use the existing shell scroll surface when constrained, otherwise the document. */
export function useMobileListScroll(key: string, entry: ListMemory, ready: boolean) {
  const restored = useRef<string | null>(null);
  useEffect(() => {
    restored.current = null;
    const shell = document.querySelector<HTMLElement>('[data-mobile-list-scroll]');
    const url = window.location.href;
    const capture = () => {
      // Navigation can shrink the document and queue a scroll event before cleanup.
      if (window.location.href !== url) return;
      const surface = shell && shell.scrollHeight > shell.clientHeight ? shell : null;
      if (restored.current === key) entry.scroll = surface ? surface.scrollTop : window.scrollY;
    };
    window.addEventListener('scroll', capture, { capture: true, passive: true });
    return () => { window.removeEventListener('scroll', capture, true); };
  }, [key, entry]);
  useEffect(() => {
    if (!ready || restored.current === key) return;
    const frame = requestAnimationFrame(() => {
      const shell = document.querySelector<HTMLElement>('[data-mobile-list-scroll]');
      if (shell && shell.scrollHeight > shell.clientHeight) shell.scrollTop = entry.scroll;
      else window.scrollTo(0, Math.min(entry.scroll, Math.max(0, document.documentElement.scrollHeight - window.innerHeight)));
      restored.current = key;
    });
    return () => cancelAnimationFrame(frame);
  }, [key, entry, ready]);
}

export function useMobileRecordNavigate() {
  const location = useLocation();
  const navigate = useMobileNavigate();
  return useCallback((to: string) => {
    window.dispatchEvent(new Event('scroll'));
    navigate(to, { state: { mobileListReturn: `${location.pathname}${location.search}`, mobileReturnFocus: document.activeElement?.id } });
  }, [location.pathname, location.search, navigate]);
}

export function useMobileListReturn(root: string) {
  const location = useLocation();
  const navigate = useMobileNavigate();
  const target = location.state?.mobileListReturn;
  // Accept only this module's list routes; direct entry always has a safe fallback.
  const allowed = [root, ...(root === '/deals' ? ['/deals/my'] : []), ...(['deals', 'projects'].includes(root.slice(1)) ? ['solar', 'electrical', 'service-maintenance', 'remove-and-reinstall'].map((type) => `${root}/${type}`) : root === '/service-maintenance' ? [`${root}/service`, `${root}/maintenance`] : [])];
  const path = typeof target === 'string' ? target.split('?')[0] : '';
  return () => navigate(allowed.includes(path) ? target : root, { state: { mobileReturnFocus: location.state?.mobileReturnFocus } });
}
